/**
 * Live "queue depth over time" chart.
 *
 * Beyond the "now" line a dashed projection extends each queue to zero at its
 * estimated clear time, so the reader can see *when* their sync will be done,
 * not just how deep the queue is. The line breaks wherever sampling stopped
 * (a paused tab, a queue reporting an error) rather than bridging the gap,
 * because a straight segment across an outage reads as a clean drain.
 *
 * The crosshair tooltip (pointer or arrow keys) is mirrored into a live
 * region for screen readers. The details table below carries the *current*
 * numbers; the time series itself is only available here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import { slateInkTokens } from '@src/theme/slateInkTheme';

import {
  formatClockTime,
  formatClockTimeWithSeconds,
  formatCount,
  queueDisplayName,
} from './queueDisplay';
import { buildSeries, computeDomain, projectedValueAt, yAxisTicks } from './queueSeries';
import type { SeriesPoint } from './queueSeries';
import type { QueueMetrics, QueueSample } from './types';

const MARGIN = { top: 22, right: 88, bottom: 28, left: 52 };
const NOW_LINE = '#B8C0CB';

const X_TICK_STEPS_MS = [
  15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 900_000, 1_800_000, 3_600_000,
];

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

interface QueueDepthChartProps {
  history: QueueSample[];
  queues: QueueMetrics[];
  height?: number;
}

const QueueDepthChart = ({ history, queues, height = 260 }: QueueDepthChartProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hoverTime, setHoverTime] = useState<number | null>(null);

  const gridColor = theme.palette.grey[200];
  const axisText = theme.palette.text.secondary;
  const forecastTint = theme.palette.grey[50];
  const surface = theme.palette.background.paper;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const series = useMemo(() => buildSeries(history, queues), [history, queues]);
  const domain = useMemo(() => computeDomain(history, series), [history, series]);

  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotHeight = height - MARGIN.top - MARGIN.bottom;

  // Scales close over the measured width, so anything that caches their output
  // has to depend on them — see the memos below.
  const xScale = useCallback(
    (t: number): number =>
      domain ?
        MARGIN.left + ((t - domain.start) / (domain.end - domain.start)) * plotWidth :
        MARGIN.left,
    [domain, plotWidth]
  );
  const yScale = useCallback(
    (v: number): number => (domain ? MARGIN.top + (1 - v / domain.yMax) * plotHeight : MARGIN.top),
    [domain, plotHeight]
  );

  /* ---------- ticks ---------- */
  const xTicks = useMemo(() => {
    if (!domain) return [];
    const span = domain.end - domain.start;
    const step = X_TICK_STEPS_MS.find(s => span / s <= 7) ?? X_TICK_STEPS_MS[X_TICK_STEPS_MS.length - 1];
    const ticks: { t: number, label: string }[] = [];
    for (let t = Math.ceil(domain.start / step) * step; t <= domain.end; t += step) {
      ticks.push({
        t,
        label: step < 60_000 ? formatClockTimeWithSeconds(t) : formatClockTime(t),
      });
    }
    return ticks;
  }, [domain]);

  const yTicks = domain ? yAxisTicks(domain) : [];

  /* ---------- hover ---------- */
  const clampHover = (t: number): number =>
    domain ? Math.min(Math.max(t, domain.start), domain.end) : t;

  const handlePointerMove = (event: React.PointerEvent<SVGRectElement>) => {
    if (!domain || plotWidth <= 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const frac = (event.clientX - rect.left) / rect.width;
    setHoverTime(clampHover(domain.start + frac * (domain.end - domain.start)));
  };

  const handleKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (!domain) return;
    const stepMs = (domain.end - domain.start) / 40;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const base = hoverTime ?? domain.now;
      setHoverTime(clampHover(base + (event.key === 'ArrowLeft' ? -stepMs : stepMs)));
    } else if (event.key === 'Escape') {
      setHoverTime(null);
    }
  };

  const hover = useMemo(() => {
    if (hoverTime == null || !domain) return null;
    const isProjected = hoverTime > domain.now;
    let snapped = hoverTime;
    const rows: { name: string, color: string, value: number | null }[] = [];
    if (isProjected) {
      series.forEach(s => rows.push({ name: s.name, color: s.color, value: projectedValueAt(s, hoverTime) }));
    } else {
      // Snap to the nearest polled sample.
      let nearest = history[0];
      history.forEach((sample) => {
        if (Math.abs(sample.time - hoverTime) < Math.abs(nearest.time - hoverTime)) nearest = sample;
      });
      snapped = nearest.time;
      series.forEach(s => rows.push({ name: s.name, color: s.color, value: nearest.pending[s.name] ?? null }));
    }
    return { time: snapped, isProjected, rows };
  }, [hoverTime, domain, series, history]);

  const hoverSummary = hover ?
    `${formatClockTimeWithSeconds(hover.time)}${hover.isProjected ? ', projected' : ''}: ${hover.rows
      .map(row => `${queueDisplayName(row.name)} ${row.value == null ? 'no data' : formatCount(row.value)}`)
      .join(', ')}` :
    '';

  /* ---------- direct end labels (drop instead of stack on collision) ---------- */
  const endLabels = useMemo(() => {
    if (!domain) return [];
    const labels = series
      .filter(s => !s.stale)
      .map((s) => {
        const last = s.actual[s.actual.length - 1];
        return { name: s.name, value: last.v, y: yScale(last.v) };
      })
      .sort((a, b) => a.y - b.y);
    // Compare against the last label actually kept, not the previous entry —
    // otherwise one dropped label cascades into dropping its neighbours.
    const kept: typeof labels = [];
    labels.forEach((label) => {
      const previous = kept[kept.length - 1];
      if (!previous || label.y - previous.y >= 14) kept.push(label);
    });
    return kept;
  }, [domain, series, yScale]);

  /* ---------- clear-time markers ---------- */
  const clearMarkers = useMemo(() => {
    if (!domain) return [];
    const markers = series
      .flatMap(s =>
        (s.projection && s.projection[1].t <= domain.end ?
          [{ name: s.name, color: s.color, t: s.projection[1].t }] :
          []))
      .sort((a, b) => a.t - b.t);
    // Stagger labels that would overlap horizontally.
    let lastX = -Infinity;
    let lastLevel = 0;
    return markers.map((marker) => {
      const x = xScale(marker.t);
      const level = x - lastX < 64 ? lastLevel + 1 : 0;
      lastX = x;
      lastLevel = level;
      return { ...marker, x, level };
    });
  }, [domain, series, xScale]);

  const linePath = (points: SeriesPoint[]): string =>
    points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.t).toFixed(1)},${yScale(p.v).toFixed(1)}`)
      .join('');

  const areaPath = (points: SeriesPoint[]): string => {
    if (points.length < 2) return '';
    const baseline = yScale(0).toFixed(1);
    return (
      `${linePath(points)}L${xScale(points[points.length - 1].t).toFixed(1)},${baseline}` +
      `L${xScale(points[0].t).toFixed(1)},${baseline}Z`
    );
  };

  if (!domain || width === 0) {
    return (
      <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height, color: 'text.secondary', gap: 0.5 }}
        >
          <Typography variant="body2">Collecting live samples…</Typography>
          <Typography variant="caption">
            The trend chart builds as the page polls the queues.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      <Box
        component="svg"
        width={width}
        height={height}
        role="group"
        aria-label="Queue depth over time, with projected time to clear per queue"
        aria-describedby="queue-depth-chart-hint"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onBlur={() => setHoverTime(null)}
        sx={{
          display: 'block',
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        {/* forecast region tint */}
        <rect
          x={xScale(domain.now)}
          y={MARGIN.top}
          width={Math.max(0, xScale(domain.end) - xScale(domain.now))}
          height={plotHeight}
          fill={forecastTint}
        />

        {/* gridlines + y labels */}
        {yTicks.map(v => (
          <g key={`y-${v}`}>
            <line
              x1={MARGIN.left}
              x2={width - MARGIN.right}
              y1={yScale(v)}
              y2={yScale(v)}
              stroke={gridColor}
              strokeWidth={1}
            />
            <text
              x={MARGIN.left - 8}
              y={yScale(v) + 3.5}
              textAnchor="end"
              fontSize={11}
              fill={axisText}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCount(v)}
            </text>
          </g>
        ))}

        {/* x ticks */}
        {xTicks.map(tick => (
          <text
            key={`x-${tick.t}`}
            x={xScale(tick.t)}
            y={height - MARGIN.bottom + 16}
            textAnchor="middle"
            fontSize={11}
            fill={axisText}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {tick.label}
          </text>
        ))}

        {/* now line */}
        <line
          x1={xScale(domain.now)}
          x2={xScale(domain.now)}
          y1={MARGIN.top - 4}
          y2={MARGIN.top + plotHeight}
          stroke={NOW_LINE}
          strokeWidth={1}
        />
        <text
          x={xScale(domain.now)}
          y={MARGIN.top - 8}
          textAnchor="middle"
          fontSize={10}
          fill={slateInkTokens.sidebarMuted}
          style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          now
        </text>

        {/* area washes (under all lines) */}
        {series.map(s =>
          s.segments.map((segment, i) => (
            <path
              key={`area-${s.name}-${i}`}
              d={areaPath(segment)}
              fill={s.color}
              opacity={s.stale ? 0.04 : 0.08}
            />
          )))}

        {/* projections (dashed, to zero) */}
        {series.map(s =>
          (s.projection ? (
            <path
              key={`proj-${s.name}`}
              d={linePath([
                s.projection[0],
                s.projection[1].t <= domain.end ?
                  s.projection[1] :
                  { t: domain.end, v: projectedValueAt(s, domain.end) ?? 0 },
              ])}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeDasharray="5 4"
              strokeLinecap="round"
              opacity={0.55}
            />
          ) : null))}

        {/* actual lines, one path per uninterrupted run of samples */}
        {series.map(s =>
          s.segments.map((segment, i) => (
            <path
              key={`line-${s.name}-${i}`}
              d={linePath(segment)}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={s.stale ? 0.4 : 1}
            />
          )))}

        {/* clear-time markers */}
        {clearMarkers.map(marker => (
          <g key={`clear-${marker.name}`}>
            <circle
              cx={marker.x}
              cy={yScale(0)}
              r={4}
              fill={surface}
              stroke={marker.color}
              strokeWidth={2}
            />
            <text
              x={marker.x}
              y={yScale(0) - 10 - marker.level * 14}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill={axisText}
            >
              {`clear ${formatClockTime(marker.t)}`}
            </text>
          </g>
        ))}

        {/* end dots + selective value labels (not for queues whose data is stale) */}
        {series.map((s) => {
          if (s.stale) return null;
          const last = s.actual[s.actual.length - 1];
          return (
            <circle
              key={`dot-${s.name}`}
              cx={xScale(last.t)}
              cy={yScale(last.v)}
              r={4}
              fill={s.color}
              stroke={surface}
              strokeWidth={2}
            />
          );
        })}
        {endLabels.map(label => (
          <text
            key={`endlabel-${label.name}`}
            x={xScale(domain.now) + 8}
            y={label.y - 7}
            fontSize={11}
            fontWeight={600}
            fill={axisText}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCount(label.value)}
          </text>
        ))}

        {/* crosshair */}
        {hover && (
          <line
            x1={xScale(hover.time)}
            x2={xScale(hover.time)}
            y1={MARGIN.top}
            y2={MARGIN.top + plotHeight}
            stroke={axisText}
            strokeWidth={1}
          />
        )}

        {/* pointer capture layer */}
        <rect
          x={MARGIN.left}
          y={MARGIN.top}
          width={plotWidth}
          height={plotHeight}
          fill="transparent"
          onPointerMove={handlePointerMove}
          onPointerLeave={() => setHoverTime(null)}
        />
      </Box>

      <Box id="queue-depth-chart-hint" sx={VISUALLY_HIDDEN}>
        Use the left and right arrow keys to move the crosshair across the chart, and Escape to
        dismiss it.
      </Box>
      <Box aria-live="polite" sx={VISUALLY_HIDDEN}>
        {hoverSummary}
      </Box>

      {/* tooltip */}
      {hover && (
        <Paper
          elevation={1}
          sx={{
            position: 'absolute',
            top: MARGIN.top,
            left: Math.min(Math.max(xScale(hover.time) + 12, MARGIN.left), Math.max(width - 190, 0)),
            px: 1.5,
            py: 1,
            pointerEvents: 'none',
            minWidth: 160,
          }}
          aria-hidden="true"
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {formatClockTimeWithSeconds(hover.time)}
            {hover.isProjected ? ' · projected' : ''}
          </Typography>
          {hover.rows.map(row => (
            <Stack key={row.name} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
              <Box sx={{ width: 12, height: 3, borderRadius: 1, bgcolor: row.color, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {row.value == null ? '—' : formatCount(row.value)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {queueDisplayName(row.name)}
              </Typography>
            </Stack>
          ))}
        </Paper>
      )}

      {/* legend */}
      <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5, px: 0.5 }}>
        {series.map(s => (
          <Stack key={`legend-${s.name}`} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 14, height: 3, borderRadius: 1, bgcolor: s.color, opacity: s.stale ? 0.4 : 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {s.stale ? `${queueDisplayName(s.name)} (unavailable)` : queueDisplayName(s.name)}
            </Typography>
          </Stack>
        ))}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <svg width={18} height={4} aria-hidden="true">
            <line
              x1={0}
              x2={18}
              y1={2}
              y2={2}
              stroke={slateInkTokens.sidebarMuted}
              strokeWidth={2}
              strokeDasharray="4 3"
            />
          </svg>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Projected drain at recent throughput
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default QueueDepthChart;
